import { Injectable } from '@angular/core';
import { TranslocoService } from '@jsverse/transloco';
import { DateTime, DurationObjectUnits } from 'luxon';
import { from, map, Observable } from 'rxjs';

import { asLabel, TranslationLoader } from '@shure/shared/angular/utils/i18n';

export const TIME_AGO_UNIT_TRANSLATIONS: { unit: keyof DurationObjectUnits; key: string }[] = [
	{ unit: 'years', key: asLabel('scAngularServices.time-ago.years-ago') },
	{ unit: 'months', key: asLabel('scAngularServices.time-ago.months-ago') },
	{ unit: 'weeks', key: asLabel('scAngularServices.time-ago.weeks-ago') },
	{ unit: 'days', key: asLabel('scAngularServices.time-ago.days-ago') },
	{ unit: 'hours', key: asLabel('scAngularServices.time-ago.hours-ago') },
	{ unit: 'minutes', key: asLabel('scAngularServices.time-ago.minutes-ago') }
];

@Injectable({ providedIn: 'root' })
export class TimeAgoService {
	private labelLoader: TranslationLoader;

	constructor(private translocoService: TranslocoService) {
		this.labelLoader = new TranslationLoader(
			'scAngularServices',
			(lang) => import(`../../i18n/${lang}`),
			translocoService
		);
	}

	public transform(utcDateTime: string | undefined): Observable<string> {
		return from(this.labelLoader.beginTranslationUsage()).pipe(
			map((labelUsage) => {
				const output = this.translate(utcDateTime);
				this.labelLoader.endTranslationUsage(labelUsage);
				return output;
			})
		);
	}

	private translate(utcDateTime: string | undefined): string {
		if (!utcDateTime) {
			return this.translocoService.translate('scAngularServices.time-ago.just-now');
		}

		const now = DateTime.utc();
		const utcDateTimeDate = DateTime.fromISO(utcDateTime, { zone: 'utc' });

		const diffInSeconds = Math.floor(now.diff(utcDateTimeDate, 'seconds').as('seconds'));
		const diffInMinutes = Math.floor(now.diff(utcDateTimeDate, 'minutes').as('minutes'));

		if (diffInSeconds < 5) {
			return this.translocoService.translate('scAngularServices.time-ago.just-now');
		}
		if (diffInMinutes < 1) {
			return this.translocoService.translate('scAngularServices.time-ago.less-than-a-minute-ago');
		}

		const diff: DurationObjectUnits = now
			.diff(
				utcDateTimeDate,
				TIME_AGO_UNIT_TRANSLATIONS.map((t) => t.unit)
			)
			.toObject();
		for (const translation of TIME_AGO_UNIT_TRANSLATIONS) {
			const diffInUnit = diff[translation.unit];
			const count = diffInUnit ? Math.floor(diffInUnit) : 0;
			if (count >= 1) {
				return this.translocoService.translate(translation.key, { count: count });
			}
		}

		return this.translocoService.translate('scAngularServices.time-ago.just-now');
	}
}
